import {
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
} from "@chakra-ui/react";
import { v4 as uuidv4 } from "uuid";
import { addNewRecordsWithFieldSpec } from "utils/apollo/cacheHelpers";
import { useAdminMutation } from "utils/apollo/hooks/admin";
import { assertPresence } from "utils/assertions";
import { DocumentType, gql } from "__generated__/gql";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import { getUpdateOrCreateVariables } from "components/Shared/Forms/AddressFieldset";
import DrawerFormFooter from "components/Shared/DrawerForm/DrawerFormFooter";
import Form, { FormFields, processApolloError } from "./Form";

const FORM_ID = "create-retailer-location-form";

const createMutation = gql(/* GraphQL */ `
  mutation CreateRetailerLocation(
    $locationId: UUID!
    $data: RetailerLocationInput!
    $distributionCenterIds: [UUID!]!
    $isAddressUpdatedOrCreated: Boolean = false
    $addressId: UUID
    $addressData: AddressPatch
    $isAdmin: Boolean = false
  ) {
    ...UpdateOrCreateAddress
    createRetailerLocation(input: { retailerLocation: $data }) {
      retailerLocation {
        id
        retailerId
        identifiers {
          id
        }
      }
    }
    setDistributionCentersForRetailerLocation(
      input: {
        retailerLocationId: $locationId
        distributionCenterIds: $distributionCenterIds
      }
    ) {
      retailerLocation {
        id
        ...RetailerDirectorySelectedLocationFragment
        ...AdminSelectedRetailerLocationContactsTable
        identifiers {
          id
          ...RetailerLocationsDirectoryTableRow
        }
      }
    }
  }
`);

type Props = {
  defaultValues?: Partial<FormFields>;
  isAdmin?: boolean;
  metadata?: GraphQLJSON;
  onCancel: () => void;
  onCompleted: (data: DocumentType<typeof createMutation>) => void;
  retailerId?: UUID;
};

export default function Create({
  defaultValues,
  isAdmin,
  metadata,
  onCancel,
  onCompleted,
  retailerId,
}: Props) {
  const [performCreate, { loading: isUpdating }] = useAdminMutation(
    createMutation,
    {
      update: (cache, result) => {
        const newLocation =
          result.data?.createRetailerLocation?.retailerLocation;

        assertPresence(newLocation);

        const newLocIds = newLocation.identifiers;

        assertPresence(newLocIds);

        addNewRecordsWithFieldSpec({
          baseFieldName: "retailerLocations",
          cache,
          id: newLocation.retailerId,
          newObjs: [newLocation],
          typename: "Retailer",
          getArgs: (defaultArgs) => [
            defaultArgs,
            { ...defaultArgs, condition: { isArchived: false } },
          ],
        });

        addNewRecordsWithFieldSpec({
          baseFieldName: "retailerLocationWithIdentifiers",
          cache,
          createdAtEnumPrefix: "RETAILER_LOCATION__CREATED_AT",
          id: newLocation.retailerId,
          newObjs: [newLocIds],
          typename: "Retailer",
        });

        addNewRecordsWithFieldSpec({
          baseFieldName: "retailerLocations",
          cache,
          newObjs: [newLocation],
          getArgs: (defaultArgs) => [
            defaultArgs,
            { ...defaultArgs, condition: { isArchived: false } },
            { ...defaultArgs, filter: { isArchived: { equalTo: false } } },
            {
              ...defaultArgs,
              filter: {
                retailerId: { equalTo: newLocation.retailerId },
                isArchived: { equalTo: false },
              },
            },
          ],
        });

        addNewRecordsWithFieldSpec({
          baseFieldName: "retailerLocationWithIdentifiers",
          cache,
          createdAtEnumPrefix: "RETAILER_LOCATION__CREATED_AT",
          newObjs: [newLocIds],
          getArgs: (defaultArgs) => [
            defaultArgs,
            {
              ...defaultArgs,
              filter: { retailerLocation: { isArchived: { equalTo: false } } },
            },
            {
              ...defaultArgs,
              filter: {
                retailerLocation: {
                  retailerId: { equalTo: newLocation.retailerId },
                  isArchived: { equalTo: false },
                },
              },
            },
          ],
        });
      },
      onCompleted,
    },
  );

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    (formData: FormFields) => {
      const locationId = uuidv4();
      const { address, distributionCenterIds, ...locationData } = formData;

      const addressVariables = getUpdateOrCreateVariables(undefined, address);

      return performCreate({
        variables: {
          locationId,
          data: {
            ...locationData,
            id: locationId,
            addressId: addressVariables.addressId,
            metadata,
          },
          distributionCenterIds,
          ...addressVariables,
          isAdmin,
        },
      });
    },
    {
      processApolloError,
      successMessage: "Retailer location created successfully",
    },
  );

  return (
    <DrawerContent bg="cardBackground">
      <DrawerCloseButton />
      <DrawerHeader>Create Retailer Location</DrawerHeader>
      <DrawerBody>
        <Form
          autoFocus
          defaultValues={defaultValues}
          onValidSubmit={handleValidSubmit}
          id={FORM_ID}
          isAdmin={isAdmin}
          retailerId={retailerId}
        />
      </DrawerBody>

      <DrawerFormFooter
        onClose={onCancel}
        formId={FORM_ID}
        submitButtonProps={{ isLoading: isUpdating }}
      />
    </DrawerContent>
  );
}
